import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Collapse, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import CRMDataGrid from './CRMDataGrid'
import { CRMTableTypes } from './types'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  CrmFilterResource,
  CrmTaskSortBy,
  listCrmTasks,
} from '@/services/crmService'
import {
  crmQueryKeys,
  useChangeCrmTaskStatus,
  useCrmReference,
} from '@/hooks/useCrm'
import { CRM_SECTION_COLORS, CRM_SECTION_TITLES } from '@/utils/crm'

const SORTABLE_FIELDS: Record<string, CrmTaskSortBy> = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  dueAt: 'dueAt',
  priority: 'priority',
}

const CRMTable = ({
  type,
  accountId,
  filters = [],
  searching = false,
  title,
}: {
  type: CRMTableTypes
  accountId: string
  /** Filters applied on top of this table's `section` filter. */
  filters?: CrmFilterResource[]
  /** The search text is typed but its request has not started yet. */
  searching?: boolean
  title?: string
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [expanded, setExpanded] = useState(false)
  const { data: reference } = useCrmReference(accountId)
  const changeStatus = useChangeCrmTaskStatus(accountId)

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    // shared with the Premium tables; the free grid refuses page sizes over 100
    pageSize: Math.min(
      Number(localStorage.getItem('table-rows-preference')) || 5,
      100
    ),
  })
  const [sortModel, setSortModel] = useState<GridSortModel>([])

  const sort = sortModel[0]
  const sortBy = (sort && SORTABLE_FIELDS[sort.field]) || 'updatedAt'
  const sortOrder = sort?.sort === 'asc' ? 'ASC' : 'DESC'

  const apiResources = useMemo<CrmFilterResource[]>(
    () => [{ name: 'section', value: [type], operator: 'EQUALS' }, ...filters],
    [type, filters]
  )

  // A new filter set starts from the first page.
  const filtersKey = JSON.stringify(filters)
  useEffect(() => {
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }))
  }, [filtersKey])

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      ...crmQueryKeys.tasks(accountId),
      apiResources,
      paginationModel.page,
      paginationModel.pageSize,
      sortBy,
      sortOrder,
    ],
    queryFn: ({ signal }) =>
      listCrmTasks(
        accountId,
        { apiResources },
        {
          page: paginationModel.page + 1,
          size: paginationModel.pageSize,
          sortBy,
          sortOrder,
        },
        signal
      ),
    placeholderData: keepPreviousData,
  })

  const BG_COLOR = isDarkMode ? '#202B40' : '#FAFBFD'
  const BORDER_COLOR = isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'
  const ICON_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: BG_COLOR,
        borderRadius: '8px',
        border: `1px solid ${BORDER_COLOR}`,
        marginTop: '30px',
        padding: '24px 30px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '19px',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          sx={{
            color: isError ? '#FE595A' : '#8F98A5',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '100%',
          }}
        >
          {isError ? 'שגיאה בטעינה' : (data?.total ?? '')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setExpanded(!expanded)}
          sx={{
            background: CRM_SECTION_COLORS[type],
            color: '#EFF2F7',
            fontFamily: 'Rubik',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            borderRadius: '4.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {title ?? CRM_SECTION_TITLES[type]}
        </Button>
        <Box
          sx={{
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: '999px',
            '&:hover': {
              backgroundColor: isDarkMode
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(107, 126, 157, 0.1)',
              color: '#5A6B85',
            },
          }}
        >
          {!expanded ? (
            <ArrowLeftIcon
              onClick={() => setExpanded(!expanded)}
              sx={{ color: ICON_COLOR, width: '30px', height: '30px' }}
            />
          ) : (
            <ArrowDropDownIcon
              onClick={() => setExpanded(!expanded)}
              sx={{ color: ICON_COLOR, width: '30px', height: '30px' }}
            />
          )}
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CRMDataGrid
          rows={data?.result ?? []}
          reference={reference}
          onRowChange={(row, patch) => {
            if (patch.statusId != null)
              void changeStatus(row.id, patch.statusId)
          }}
          loading={isFetching || searching}
          paging={{
            rowCount: data?.total ?? 0,
            paginationModel,
            onPaginationModelChange: setPaginationModel,
            sortModel,
            onSortModelChange: model => {
              setSortModel(model)
              setPaginationModel(prev => ({ ...prev, page: 0 }))
            },
          }}
        />
      </Collapse>
    </Box>
  )
}

export default CRMTable
